import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Svg, Typography } from '@altermaind/web/ui';
import { Routes } from '@/_shared';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Charts } from '@/_shared/components/Charts';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { ChipsTemplate } from '@/surveys/components';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  getSurveysCampaignsCall,
  getTemplatesCall,
} from '@/surveys/state';

type Props = {
  hasSoftAndHardSkillCampaigns: boolean;
};

export const OverviewSurveys: FC<Props> = ({ hasSoftAndHardSkillCampaigns }) => {
  const t = useTranslations('dashboard.overviewSurveys');
  const navigate = useNavigate();
  const { genericResponsiveValue } = useResponsiveStyle();

  const { response: responseGetTemplates } = getTemplatesCall.hook();
  const { response: responseGetSurveysCampaigns } = getSurveysCampaignsCall.hook();

  const [activeChip, setActiveChip] = useState<string>('');

  const {
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    response: responseGetSurveyCampaignByIdResponsesResultsStats,
    loading: loadingGetSurveyCampaignByIdResponsesResultsStats,
    organizationStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const totalInvitation =
    responseGetSurveyCampaignByIdResponsesResultsStats?.organizationStats?.totalInvited ?? 0;
  const safeCompleted =
    responseGetSurveyCampaignByIdResponsesResultsStats?.organizationStats?.totalCompletedSurveys ??
    0;

  const goToSurveyDetails = useCallback(() => {
    const templateId = responseGetTemplates?.find(template => template.name === activeChip)?.id;
    if (templateId) {
      navigate(Routes.SurveyDetail.replace(':surveyId', templateId));
    }
  }, [activeChip, navigate, responseGetTemplates]);

  const getDescriptionMetric = useCallback(
    (surveyTemplateName: string, metricName: string) => {
      if (!responseGetTemplates) {
        return '';
      }
      return (
        responseGetTemplates
          ?.find(template => template.name === surveyTemplateName)
          ?.metrics?.find(metric => metric.name === metricName)?.description ?? ''
      );
    },
    [responseGetTemplates],
  );

  useEffect(() => {
    if (responseGetSurveysCampaigns && responseGetSurveysCampaigns.length > 0) {
      if (hasSoftAndHardSkillCampaigns) {
        const templateId = responseGetTemplates?.find(template => template.name === activeChip)?.id;
        const campaign = responseGetSurveysCampaigns.findLast(
          currentCampaign => currentCampaign.surveyTemplateId === templateId,
        );

        if (campaign && campaign.id) {
          if (campaign.id) {
            executeGetSurveyCampaignByIdResponsesResultsStats({
              campaignId: campaign.id,
            });
          }
        }
      } else {
        const campaign =
          responseGetSurveysCampaigns &&
          responseGetSurveysCampaigns.length > 0 &&
          responseGetSurveysCampaigns[0];

        if (campaign && campaign.id) {
          const templateName = responseGetTemplates?.find(
            template => template.id === campaign.surveyTemplateId,
          )?.name;

          setActiveChip(templateName || '');

          executeGetSurveyCampaignByIdResponsesResultsStats({
            campaignId: campaign.id,
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeChip]);

  return (
    <Box mt='m'>
      {hasSoftAndHardSkillCampaigns && (
        <>
          <Typography variant='h2'>{t('title')}</Typography>
          <Typography mb={!hasSoftAndHardSkillCampaigns ? 's' : 'none'} mt='xs' variant='p3/light'>
            {t('description')}
          </Typography>
        </>
      )}
      {hasSoftAndHardSkillCampaigns && (
        <ChipsTemplate setActiveChip={setActiveChip} activeChip={activeChip} />
      )}
      {loadingGetSurveyCampaignByIdResponsesResultsStats ? (
        <Box
          height='-webkit-fill-available'
          display='flex'
          justifyContent='center'
          alignItems='center'
          minHeight={300}
        >
          <Loader size='s' />
        </Box>
      ) : (
        <>
          {safeCompleted > 0 && (
            <Box mb='m'>
              <Box display='flex' flexDirection='row' alignItems='center'>
                <Typography variant='h3'>
                  {t('results.completedBox.title', { surveyTemplateName: activeChip })}
                </Typography>
                <Box ml='xs' alignItems='center' flexDirection='row' display='flex'>
                  <Svg name='users' size='s' />
                  <Typography ml='xxs' variant='h3'>
                    {safeCompleted}
                  </Typography>
                </Box>
              </Box>
              <Typography mt='xs' variant='p3/light'>
                {t('results.completedBox.description', {
                  surveyTemplateName: activeChip,
                  metricsCount: organizationStats?.metricsScore?.length ?? 0,
                })}
              </Typography>
            </Box>
          )}
          <Box
            id='survey-box'
            data-testid='overview-surveys-results-box'
            mt={hasSoftAndHardSkillCampaigns ? 'none' : 's'}
            display='flex'
            flexDirection={genericResponsiveValue({
              breakPoints: { desktop: 'row' },
              defaultValue: 'column',
            })}
          >
            {safeCompleted > 0 && (
              <Box
                mr={genericResponsiveValue({
                  breakPoints: { desktop: 's' },
                  defaultValue: 'none',
                })}
                alignItems='center'
                flex='1.3'
              >
                <Charts.RadarChartComponent
                  data={(organizationStats?.metricsScore ?? []).map(metric => {
                    return {
                      subject: metric.metric?.name ?? '',
                      current: metric.score ?? 0,
                      expected: 7,
                      description: getDescriptionMetric(
                        activeChip || '',
                        metric.metric?.name || '',
                      ),
                    };
                  })}
                />
              </Box>
            )}
            <Box
              mt={
                safeCompleted > 0
                  ? genericResponsiveValue({
                      breakPoints: { desktop: 'none' },
                      defaultValue: 'm',
                    })
                  : 'none'
              }
              alignItems='center'
              flex='1'
              data-testid='survey-results-card'
            >
              <Card>
                <Box
                  display='flex'
                  flexDirection={genericResponsiveValue({
                    breakPoints: { desktop: 'row' },
                    defaultValue: 'column',
                  })}
                >
                  <Box flex='1'>
                    <Typography variant='h3'>
                      {t('results.title', { surveyTemplateName: activeChip })}
                    </Typography>
                    <Typography variant='p2/light'>
                      {t(
                        totalInvitation === 1
                          ? 'results.description.singular'
                          : 'results.description.plural',
                        {
                          total: totalInvitation,
                        },
                      )}
                    </Typography>
                    <Charts.HalfPieChartComponent
                      data={[
                        {
                          name: t('results.completed'),
                          value: safeCompleted,
                        },
                        {
                          name: t('results.notCompleted'),
                          value: totalInvitation - safeCompleted,
                        },
                      ]}
                      colors={['primary', 'white']}
                      centerValue={`${((safeCompleted / totalInvitation) * 100).toFixed(0)}%`}
                      centerLabelValue={`${t('results.completionRate')}`}
                    />
                    <Box display='flex' justifyContent='center' mt='s'>
                      <Button onClick={goToSurveyDetails} mb='s' mt='s' variant='primary'>
                        {t('results.cta', { surveyTemplateName: activeChip })}
                      </Button>
                    </Box>
                    {safeCompleted === 0 && (
                      <AlertBadge
                        type='transparent'
                        iconName='info'
                        label={t('results.info.noData')}
                      />
                    )}
                  </Box>
                </Box>
              </Card>
            </Box>
            {safeCompleted === 0 && <Box flex='1' />}
          </Box>
        </>
      )}
    </Box>
  );
};
